import type { TOC } from '@ember/component/template-only';
import { concat, fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import {
  PrismBanner,
  PrismButton,
  PrismButtonGroup,
  PrismCard,
  PrismComboSelectInput,
  PrismLoadingBar,
  PrismModal,
  PrismSelectInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { and, or } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralCurrencyPicker from 'lumen/components/general/currency-picker';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDismissibleBanner from 'lumen/components/general/dismissible-banner';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralReportScheduleReportsModals from 'lumen/components/general/report-schedule/reports-modals';
import HotelAndMarketCurrencyCustomCurrencyPicker from 'lumen/components/hotel-and-market/currency/custom-currency-picker';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import HotelAndMarketRevenueInsightChartContainer from 'lumen/components/hotel-and-market/revenue-insight/chart-container';
import HotelAndMarketRevenueInsightChartsChartPace from 'lumen/components/hotel-and-market/revenue-insight/charts/chart-pace';
import HotelAndMarketRevenueInsightChartsChartPaceCancellations from 'lumen/components/hotel-and-market/revenue-insight/charts/chart-pace-cancellations';
import HotelAndMarketRevenueInsightChartsChartStayDate from 'lumen/components/hotel-and-market/revenue-insight/charts/chart-stay-date';
import HotelAndMarketRevenueInsightDatepickersTargetPicker from 'lumen/components/hotel-and-market/revenue-insight/datepickers/target-picker';
import HotelAndMarketRevenueInsightFixedKpiHeader from 'lumen/components/hotel-and-market/revenue-insight/fixed-kpi-header';
import HotelAndMarketRevenueInsightKpiKpiSection from 'lumen/components/hotel-and-market/revenue-insight/kpi/kpi-section';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import HotelAndMarketRevenueInsightPickUpPaceEmptyState from 'lumen/components/hotel-and-market/revenue-insight/pick-up-pace-empty-state';
import HotelAndMarketRevenueInsightSectionTitle from 'lumen/components/hotel-and-market/revenue-insight/section-title';
import HotelAndMarketRevenueInsightSegmentationSegmentationBlock from 'lumen/components/hotel-and-market/revenue-insight/segmentation/segmentation-block';
import HotelAndMarketRevenueInsightSortAndReorderBlocks from 'lumen/components/hotel-and-market/revenue-insight/sort-and-reorder-blocks';
import SettingsMappingModalsKpiSectionModal from 'lumen/components/settings/mapping/modals/kpi-section-modal';
import SettingsMappingModalsMappingTypeModal from 'lumen/components/settings/mapping/modals/mapping-type-modal';
import type HotelRevenueInsightController from 'lumen/controllers/hotel/revenue-insight';
import can from 'lumen/helpers/can';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import getElement from 'lumen/modifiers/get-element';
import scrollPosition from 'lumen/modifiers/scroll-position';
import type HotelRevenueInsightRoute from 'lumen/routes/hotel/revenue-insight';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';
import {
  ALL_SELECTABLE_SEGMENTATION_BLOCKS,
  RI_SECTIONS,
} from 'lumen/utils/constants/ri-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

interface HotelRevenueInsightSignature {
  Args: {
    controller: HotelRevenueInsightController;
    // @ts-expect-error: Incorrect type
    model: ModelFrom<HotelRevenueInsightRoute>;
  };
}

<template>
  {{#if @controller.riSelections.isCancellationsOnlyView}}
    {{pageTitle
      (contextualPageTitle
        (t "revenue_insight.view_selection.cancellations_only")
      )
    }}
  {{else}}
    {{pageTitle
      (contextualPageTitle (t "revenue_insight.view_selection.net_bookings"))
    }}
  {{/if}}
  <GeneralPageHeader
    class="revenue-insight"
    role="tooltip"
    {{on "mouseenter" (fn @controller.setIsFilterBarHovered true)}}
    {{on "mouseleave" (fn @controller.setIsFilterBarHovered false)}}
  >
    <div class="content-wrapper relative flex h-[60px]">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        <div class="ic-product-tour-ri-single-datepicker mr-200">
          {{! @glint-expect-error: Incorrect type }}
          <GeneralDatepickersDatepickerWithSidebar
            @displayEvents={{true}}
            @maxDate={{@controller.datePickerMaxDate}}
            @maxRange="2 years"
            @minDate={{@controller.datePickerMinDate}}
            @range={{@controller.range}}
            @rangeChanged={{@controller.rangeChanged}}
            @selectSingleDay={{false}}
            @sidebarLists={{@controller.datePickerRangeOptions}}
          />
        </div>
      </div>
      <div
        class="ri-single-reference-datepicker ic-product-tour-ri-single-reference-datepicker ic-product-tour-ri-single-cancellations-reference-datepicker"
      >
        {{! @glint-expect-error: Incorrect type }}
        <HotelAndMarketRevenueInsightDatepickersTargetPicker
          @displayEvents={{false}}
          @hasBudgetForecast={{@controller.model.hasBudgetForecastTask.last}}
          @maxDate={{@controller.datePickerRefMaxDate}}
          @maxRange="2 years"
          @minDate={{@controller.datePickerRefMinDate}}
          @nrOfDays={{@controller.referencePeriod}}
          @range={{@controller.refRange}}
          @rangeChanged={{@controller.refRangeChanged}}
          @selectedOptionId={{@controller.reference}}
          @setHidePickupLY={{@controller.setHidePickupLY}}
          @sidebarLists={{@controller.refDatePickerRangeOptions}}
        />
      </div>
      <div
        class="filter-bar-wrapper ic-product-tour-ri-filter-favourites-button ic-product-tour-ri-single-filter-bar-container"
      >
        <GeneralFilterBarFilterBar
          @activeFilters={{@controller.activeFilters}}
          @favourites={{@controller.favourites}}
          {{! @glint-expect-error: Incorrect type }}
          @filterBarSegmentationClicked={{@controller.filterBarSegmentationClicked}}
          @filtersToCheckForVisibility={{ALL_SELECTABLE_SEGMENTATION_BLOCKS}}
          {{! @glint-expect-error: Incorrect type }}
          @hotel={{@controller.hotelOrMarket}}
          @isFavouritesDisabled={{@controller.isFavouritesDisabled}}
          @isHovered={{@controller.isFilterBarHovered}}
          @isLoading={{@controller.isModelLoading}}
          @pageType={{FILTER_BAR_PAGETYPES.REVENUE_INSIGHT}}
          @segmentationFilters={{@controller.segmentationFilters}}
          {{! @glint-expect-error: Incorrect type }}
          @setFilterValues={{@controller.setFilterValues}}
          @stayDateFilter={{@controller.stayDateFilter}}
          @visibleFilters={{@controller.segmentationOrder}}
        />
      </div>
      <div class="flex flex-1 items-center justify-end gap-200">
        {{#if @controller.riSelections.isCancellationsOnlyView}}
          <PrismButtonGroup
            {{! @glint-expect-error: Incorrect type }}
            @onChange={{@controller.toggleCancellationsView}}
            @value={{@controller.cancellationsView}}
            as |ButtonGroup|
          >
            <ButtonGroup.button @label="#" @value="absolute" class="w-[32px]" />
            <ButtonGroup.button
              @label="%"
              @value="percentage"
              class="ic-product-tour-ri-single-cancellations-view-btn-percentage w-[32px]"
              id="percentage-button"
            />
            <EmberTooltip
              @side="left"
              @targetId="percentage-button"
              {{! @glint-expect-error: Incorrect type }}
              @text={{t "revenue_insight.cancellations_percentage.explanation"}}
              @tooltipClass="prism-tooltip"
            />
          </PrismButtonGroup>
        {{/if}}

        {{#if @controller.hasCustomCurrencyFeatureEnabled}}
          <HotelAndMarketCurrencyCustomCurrencyPicker
            @fallbackCurrencyCode={{@controller.riCurrency}}
            @isReadOnly={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @onCurrencyChange={{fn (mut @controller.currency)}}
            @onProfileChange={{fn (mut @controller.profile)}}
            @selectedCurrencyCode={{or
              @controller.currency
              @controller.riCurrency
            }}
            @selectedProfileId={{@controller.profile}}
          />
        {{else}}
          <GeneralCurrencyPicker
            @defaultCurrencyTooltip={{t
              "revenue_insight.currency.local_in_use"
            }}
            @fallbackCurrencyCode={{@controller.riCurrency}}
            {{! @glint-expect-error: Incorrect type }}
            @onCurrencyChange={{fn (mut @controller.currency)}}
          />
        {{/if}}

        <GeneralFilterBarDivider />

        {{#if @controller.riSelections.isCancellationsOnlyView}}
          {{#if @controller.riSelections.isCancellationsAbsoluteView}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="export"
              @label={{t "general.export_group.export"}}
              @onClick={{@controller.exportBasicReport}}
              class="ic-product-tour-ri-single-export text-nowrap"
            />
          {{else}}
            <div class="flex items-center">
              <PrismButton
                @buttonType="secondary"
                @iconBefore="export"
                @isDisabled={{true}}
                @label={{t "general.export_group.export"}}
                class="ic-product-tour-ri-single-export text-nowrap"
              />
              <PrismTooltip
                @side="bottom"
                @text={{t
                  "revenue_insight.export.unavailable_for_percentage_view"
                }}
              />
            </div>
          {{/if}}
        {{else}}
          {{#if @controller.riSelections.isTargetActive}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="export"
              @label={{t "general.export_group.export"}}
              @onClick={{@controller.exportBasicReport}}
              class="ic-product-tour-ri-single-export text-nowrap"
            />
          {{else}}
            <GeneralDropdownsDropdownExport
              @export={{@controller.handleExport}}
              @exportChoices={{@controller.exportChoices}}
              @isScheduleDisabled={{hash disabled=false}}
              class="ic-product-tour-ri-single-export"
            />
          {{/if}}
        {{/if}}
      </div>
    </div>
  </GeneralPageHeader>
  <div class="insight-page-sticky-header">
    <HotelAndMarketRevenueInsightFixedKpiHeader
      {{! @glint-expect-error: Incorrect type }}
      @allSegmentationData={{@controller.kpis.all}}
      @changeSelectedKpi={{@controller.changeSelectedKpi}}
      @changeSelectedKpiCategory={{@controller.changeSelectedKpiCategory}}
      @kpi={{@controller.kpi}}
      {{! @glint-expect-error: Incorrect type }}
      @kpiBlocks={{@controller.kpiBlocks}}
      @kpiCategory={{@controller.kpiCategory}}
    />
    {{#if @controller.isNetBookingsBannerVisible}}
      {{! @glint-expect-error: Incorrect type }}
      <GeneralDismissibleBanner
        @bannerMessage={{t
          "revenue_insight.net_bookings_explanation"
          integrationDate=@controller.riSelections.formattedIntegrationDate
          numberOfDays=@controller.numberOfDaysSelected
        }}
        @hasCtaButtons={{true}}
        @intercomArticleLink="en/articles/6949233-what-is-net"
        @onDismiss={{@controller.handleNetBookingsGotItClicked}}
        @shouldShowIntercomLink={{true}}
      />
    {{/if}}
    {{#if @controller.isNetCancellationsBannerVisible}}
      {{! @glint-expect-error: Incorrect type }}
      <GeneralDismissibleBanner
        @bannerMessage={{t
          "revenue_insight.net_cancellations_explanation"
          integrationDate=@controller.riSelections.formattedIntegrationDate
          numberOfDays=@controller.numberOfDaysSelected
        }}
        @intercomArticleLink="en/articles/6949233-what-is-net"
        @onDismiss={{@controller.handleNetCancellationsGotItClicked}}
        @shouldShowIntercomLink={{true}}
      />
    {{/if}}
  </div>
  <GeneralPageMain
    {{didInsert @controller.setExportChoices}}
    {{didInsert @controller.updateDisplayedSegmentationFilters}}
    {{didUpdate
      @controller.updateDisplayedSegmentationFilters
      @controller.segmentationFilters
      @controller.stayDateFilter
      @controller.riSelections.isToDateInFuture
      @controller.riSelections.isCancellationsOnlyView
      @controller.isModelLoading
    }}
    {{scrollPosition @controller.handleOnPageScrollEvent}}
  >
    <div class="revenue-insight content-wrapper main-content">
      {{#if @controller.isModelLoading}}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
      {{/if}}
      {{#if @controller.isClosed}}
        <div class="mb-500">
          <PrismBanner
            @bannerMessage={{t "revenue_insight.closed_message"}}
            @bannerStyle="warning"
          />
        </div>
      {{/if}}

      {{! @glint-expect-error: Incorrect type }}
      {{#if @controller.showNoGroupReportsMessage}}
        <div class="revenue-insight-message-container">
          {{t "revenue_insight.no_group_reports.title"}}
          <br />
          <br />
          <span class="description-text">
            {{t "revenue_insight.no_group_reports.message"}}
          </span>
        </div>
      {{else}}
        <section class="mb-800">
          <div class="section-title mt-0">
            <HotelAndMarketRevenueInsightSectionTitle
              {{! @glint-expect-error: Incorrect type }}
              @infoIconTooltipContent={{t
                "revenue_insight.tooltip.cancellations_reservation"
              }}
              @section={{RI_SECTIONS.KPI}}
              @shouldShowInfoIconTooltip={{@controller.riSelections.isCancellationsOnlyView}}
              @titleContent={{t "revenue_insight.headers.kpis"}}
            >
              <div
                class="ri-info flex items-center justify-end gap-200"
                slot="extra-info"
              >
                <p
                  class="ri-info__last-update-timestamp text-200--medium ic-product-tour-ri-single-last-update-timestamp-info flex items-center gap-100"
                >
                  {{#if @controller.isOutOfSync}}
                    <GeneralIconWithTooltip
                      @iconName="sync-disabled"
                      @side="top"
                      @tooltipContent={{t
                        "revenue_insight.out_of_sync.tooltip"
                      }}
                    />
                    <p>
                      {{t "revenue_insight.synced_as_of"}}
                      {{@controller.formattedUpdateTimestamp}}
                    </p>
                  {{else}}
                    {{#if @controller.isDemoProperty}}
                      {{t "general.demo_hotel"}},
                    {{else if @controller.extractDate}}
                      {{t
                        "revenue_insight.extract_date"
                      }}{{@controller.extractDate}},
                    {{/if}}
                    {{#if @controller.formattedUpdateTimestamp}}
                      {{t "revenue_insight.synced_as_of"}}
                      {{@controller.formattedUpdateTimestamp}}
                    {{/if}}
                  {{/if}}
                </p>
                {{#if @controller.hasKPISettingsAccess}}
                  <PrismButton
                    @buttonType="secondary"
                    @iconBefore="sliders"
                    @label={{t "revenue_insight.menu.preferences"}}
                    @onClick={{fn
                      (mut @controller.isShowingKpiSectionModal)
                      true
                    }}
                  />
                {{/if}}
              </div>
            </HotelAndMarketRevenueInsightSectionTitle>
          </div>
          <div
            class="kpi-section-wrapper ic-product-tour-ri-single-kpi-section ic-product-tour-ri-single-cancellations-kpi-section"
          >
            <HotelAndMarketRevenueInsightKpiKpiSection
              @changeSelectedKpi={{@controller.changeSelectedKpi}}
              @changeSelectedKpiCategory={{@controller.changeSelectedKpiCategory}}
              {{! @glint-expect-error: Incorrect type }}
              @data={{get @controller.kpis.all "0"}}
              @isLoading={{@model.kpis.isRunning}}
              @kpi={{@controller.kpi}}
              @kpiBlocks={{@controller.kpiBlocks}}
              @kpiCategory={{@controller.kpiCategory}}
              @visibleKpis={{get
                @controller.visibleKpisPerBreakdown
                RI_BREAKDOWNS.ALL
              }}
            />
          </div>
        </section>

        <section class="mb-800">
          <div class="section-title">
            <HotelAndMarketRevenueInsightSectionTitle
              @section={{RI_SECTIONS.STAY_DATE}}
              @titleContent={{t "revenue_insight.impact_date"}}
            >
              <div class="stay-date-aggregation-container" slot="extra-info">
                <span class="stay-date-dropdown-label text-300--regular">
                  {{t "parity.view_by"}}
                </span>
                <PrismSelectInput
                  @horizontalPosition="right"
                  @onChange={{@controller.stayDateAggregationKeyChanged}}
                  @options={{@controller.stayDateAggregationOptions}}
                  @value={{@controller.selectedStayDateAggregationInDropdown}}
                />
              </div>
            </HotelAndMarketRevenueInsightSectionTitle>
          </div>
          <div class="section-content">
            <PrismCard
              class="revenue-insight-chart-outer-container stay-date ic-product-tour-ri-single-stay-date-chart-container"
            >
              <:header as |Header|>
                <div class="title-box">
                  <div class="flex items-center justify-start">
                    {{#if @model.kpis.isRunning}}
                      <HotelAndMarketRevenueInsightLoadingStateTitle />
                    {{else}}
                      <Header.title>
                        {{t "revenue_insight.impact_date"}}
                      </Header.title>
                    {{/if}}
                  </div>
                </div>
              </:header>

              <:content>
                <div class="stay-date-chart-container">
                  {{#unless @model.kpis.isRunning}}
                    <HotelAndMarketRevenueInsightChartsChartStayDate
                      @aggregationPeriod={{@controller.stayDateAggregation}}
                      {{! @glint-expect-error: Incorrect type }}
                      @chartSelectionChanged={{@controller.chartSelectionChanged}}
                      {{! @glint-expect-error: Incorrect type }}
                      @data={{@controller.stayDateData}}
                      @filterLimits={{@controller.filterLimits}}
                      @filtersActive={{@controller.filtersActive}}
                      @fromDate={{@controller.parsedFromDate}}
                      @isLoading={{@model.kpis.isRunning}}
                      @isRatesEnabled={{@controller.access.hasRates}}
                      @kpi={{@controller.kpi}}
                      @kpiCategory={{@controller.kpiCategory}}
                      @openDayDetail={{@controller.openDayDetail}}
                      @parsedStayDateFilter={{@controller.parsedStayDateFilter}}
                      @reference={{@controller.reference}}
                      @toDate={{@controller.parsedToDate}}
                      {{! @glint-expect-error: Incorrect type }}
                      @visibleKpis={{get
                        @controller.visibleKpisPerBreakdown
                        RI_BREAKDOWNS.IMPACT_DATE
                      }}
                    />
                  {{/unless}}
                </div>
              </:content>
            </PrismCard>
          </div>
        </section>

        <section
          class="mb-800"
          {{getElement @controller.setPickupPaceSectionElement}}
        >
          <div class="section-title">
            <HotelAndMarketRevenueInsightSectionTitle
              @section={{RI_SECTIONS.PICKUP}}
              @titleContent={{@controller.pickupAndPaceTranslation}}
            />
          </div>
          {{#if @controller.shouldShowPickupAndPaceInaccurateDataState}}
            <div class="section-banner mb-400">
              <PrismBanner
                @bannerMessage={{t
                  "revenue_insight.pickup_and_pace.incomplete"
                  integrationDate=@controller.riSelections.formattedIntegrationDate
                }}
                @bannerStyle="info"
                @link={{@controller.intercomLink}}
              />
            </div>
          {{/if}}
          <div
            class="section-content pick-up pace
              {{unless
                @controller.riSelections.isCancellationsPercentageView
                ' insight-grid'
              }}"
          >
            {{#unless @controller.riSelections.isCancellationsPercentageView}}
              <HotelAndMarketRevenueInsightSegmentationSegmentationBlock
                @cardContentClassName="ic-product-tour-ri-single-pickup-block-content ic-product-tour-ri-single-cancellations-recent-cancellations-block-content"
                @filterLimits={{@controller.filterLimits}}
                @isLoading={{@model.pickup.isRunning}}
                @kpi={{get @controller.kpiPerBreakdown RI_BREAKDOWNS.PICKUP}}
                @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
                @searchValues={{get
                  @controller.searchValuesPerSegmentationId
                  RI_BREAKDOWNS.PICKUP
                }}
                @section={{RI_SECTIONS.PICKUP}}
                {{! @glint-expect-error Incorrect type }}
                @segmentation={{@controller.sortedSegmentationPickup}}
                @segmentationFilterClicked={{@controller.segmentationFilterClicked}}
                {{! @glint-expect-error Incorrect type }}
                @setIsSearchActive={{@controller.setIsSearchActive}}
                {{! @glint-expect-error Incorrect type }}
                @setSearchValue={{@controller.setSearchValue}}
                @visibleKpis={{get
                  @controller.visibleKpisPerBreakdown
                  RI_BREAKDOWNS.PICKUP
                }}
              />
            {{/unless}}

            <HotelAndMarketRevenueInsightChartContainer
              @kpi={{@controller.kpi}}
              @showLoadingPlaceholderState={{@model.pace.isRunning}}
              class="revenue-insight-chart-outer-container revenue-chart pace ic-product-tour-ri-single-pace-chart-container ic-product-tour-ri-single-cancellations-pace-chart-container col-span-2"
              data-test-id="pace-chart-container"
            >
              {{#if @controller.shouldShowPaceEmptyState}}
                <HotelAndMarketRevenueInsightPickUpPaceEmptyState
                  @hasPickup={{true}}
                  class="h-full"
                />
              {{else}}
                {{#if @controller.riSelections.isCancellationsOnlyView}}
                  <HotelAndMarketRevenueInsightChartsChartPaceCancellations
                    {{! @glint-expect-error: Incorrect type }}
                    @chartSelectionChanged={{@controller.chartSelectionChanged}}
                    {{! @glint-expect-error: Incorrect type }}
                    @data={{@controller.pace}}
                    @filterLimits={{@controller.filterLimits}}
                    {{! @glint-expect-error: Incorrect type }}
                    @kpi={{get @controller.kpiPerBreakdown RI_BREAKDOWNS.PACE}}
                    {{! @glint-expect-error: Incorrect type }}
                    @visibleKpis={{get
                      @controller.visibleKpisPerBreakdown
                      RI_BREAKDOWNS.PACE
                    }}
                  />
                {{else}}
                  <HotelAndMarketRevenueInsightChartsChartPace
                    {{! @glint-expect-error: Incorrect type }}
                    @allSegmentation={{@controller.kpis.all}}
                    {{! @glint-expect-error: Incorrect type }}
                    @chartSelectionChanged={{@controller.chartSelectionChanged}}
                    {{! @glint-expect-error: Incorrect type }}
                    @data={{@controller.pace}}
                    @filterLimits={{@controller.filterLimits}}
                    @filtersActive={{@controller.filtersActive}}
                    @fromDate={{@controller.fromDate}}
                    @isLoading={{@model.pace.isRunning}}
                    {{! @glint-expect-error: Incorrect type }}
                    @kpi={{get @controller.kpiPerBreakdown RI_BREAKDOWNS.PACE}}
                    @kpiCategory={{@controller.kpiCategory}}
                    @stayDateFilter={{@controller.stayDateFilter}}
                    @toDate={{@controller.toDate}}
                    {{! @glint-expect-error: Incorrect type }}
                    @visibleKpis={{get
                      @controller.visibleKpisPerBreakdown
                      RI_BREAKDOWNS.PACE
                    }}
                  />
                {{/if}}
              {{/if}}
            </HotelAndMarketRevenueInsightChartContainer>
          </div>
        </section>

        <section>
          <div class="section-title">
            <div
              class="ic-product-tour-ri-single-cancellations-breakdown-settings flex items-center justify-between"
            >
              <HotelAndMarketRevenueInsightSectionTitle
                @section={{RI_SECTIONS.SEGMENTATION}}
                @titleContent={{t "revenue_insight.headers.segments"}}
              />

              <PrismComboSelectInput
                @horizontalPosition="right"
                @onChange={{@controller.breakdownSettingsOptionsChanged}}
                @options={{@controller.breakdownSettingsOptions}}
                @value={{hash
                  icon="sliders"
                  label=(t "revenue_insight.menu.preferences")
                }}
              />
            </div>
          </div>
          <div
            class="section-content insight-grid ic-product-tour-ri-single-breakdown-section"
          >
            {{#each
              @controller.visibleSegmentationIds
              as |segmentationId index|
            }}
              <HotelAndMarketRevenueInsightSegmentationSegmentationBlock
                @filterLimits={{@controller.filterLimits}}
                @isLoading={{@model.breakdowns.isRunning}}
                @kpi={{get @controller.kpiPerBreakdown segmentationId}}
                @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
                {{! @glint-expect-error Incorrect type }}
                @searchValues={{get
                  @controller.searchValuesPerSegmentationId
                  segmentationId
                }}
                {{! @glint-expect-error Incorrect type }}
                @segmentation={{get
                  @controller.sortedVisibleSegmentations
                  segmentationId
                }}
                @segmentationBlockIdx={{index}}
                @segmentationFilterClicked={{@controller.segmentationFilterClicked}}
                {{! @glint-expect-error Incorrect type }}
                @setIsSearchActive={{@controller.setIsSearchActive}}
                {{! @glint-expect-error Incorrect type }}
                @setSearchValue={{@controller.setSearchValue}}
                @visibleKpis={{get
                  @controller.visibleKpisPerBreakdown
                  segmentationId
                }}
                data-test-id={{concat "segmentation-block-" segmentationId}}
              />
            {{/each}}
          </div>
        </section>
      {{/if}}
    </div>
  </GeneralPageMain>

  {{! @glint-expect-error: Incorrect type }}
  <GeneralReportScheduleReportsModals
    @canCreateNewUser={{can "hotel.reports.create.other.new"}}
    @canCreateOtherReports={{can "hotel.reports.create.other.existing"}}
    @isShowingScheduleModal={{@controller.scheduleReport}}
    @onCloseScheduleModal={{@controller.closeScheduleModal}}
    @type="revenueinsight"
  />
  {{#if @controller.isShowingSelectionAndSortModal}}
    {{!
    Modal should have the height of the content
    The content does not expand or change in a way that changes the height
    This would give too many complications with the width calculation and would result in a permanent scrollbar
  }}
    <PrismModal
      @onClose={{@controller.closeSelectionAndSortModal}}
      @title={{t "revenue_insight.breakdown_settings.title"}}
    >
      <HotelAndMarketRevenueInsightSortAndReorderBlocks
        @closeModal={{@controller.closeSelectionAndSortModal}}
        @doRefresh={{@controller.refreshRoute}}
        @segmentationBlocksOrder={{@controller.segmentationBlocksOrder}}
      />
    </PrismModal>
  {{else if @controller.isShowingKpiSectionModal}}
    {{! @glint-expect-error: Incorrect type }}
    <SettingsMappingModalsKpiSectionModal
      @onCloseModal={{fn (mut @controller.isShowingKpiSectionModal) false}}
      @refreshRoute={{@controller.refreshRoute}}
    />
  {{/if}}

  {{#if
    (and
      @controller.isMappingTypeModalAvailable
      @controller.isShowingReviMappingTypeModal
    )
  }}
    <SettingsMappingModalsMappingTypeModal
      @onCloseModal={{@controller.onCloseReviMappingTypeModal}}
    />
  {{/if}}

  {{outlet}}
</template> satisfies TOC<HotelRevenueInsightSignature>;
